/**
 * CSRF-aware fetch wrapper
 * Automatically extracts CSRF tokens from responses and includes them in subsequent requests
 * 
 * Usage:
 *   // First call (GET) extracts token from response header
 *   const roles = await csrfFetch('/api/roles');
 *   
 *   // Subsequent calls (POST/PUT/DELETE) include token automatically
 *   await csrfFetch('/api/roles', {
 *       method: 'POST',
 *       headers: { 'Content-Type': 'application/json' },
 *       body: JSON.stringify({ name: 'New Role' })
 *   });
 */

let csrfToken = '';

/**
 * Extract CSRF token from response headers
 */
function extractCSRFToken(response: Response): void {
    const token = response.headers.get('X-CSRF-Token');
    
    if (token) {
        csrfToken = token;
    } else {
        console.warn('[csrfFetch] No CSRF token in response headers!');
    }
}

/**
 * Fetch wrapper that automatically handles CSRF tokens
 * - Extracts X-CSRF-Token from all responses and stores it
 * - Includes X-CSRF-Token in all subsequent state-changing requests (POST, PUT, DELETE, PATCH)
 * - Does NOT include token in GET requests (no CSRF risk)
 * 
 * @param url - The URL to fetch
 * @param options - Standard fetch options
 * @returns The Response object
 * @throws Error if the response is not ok
 */
export async function csrfFetch(url: string, options: RequestInit = {}): Promise<Response> {
    try {
        // Build headers, preserving any existing ones
        const headers = options.headers ? { ...options.headers } : {};
        const method = (options.method || 'GET').toUpperCase();
        
        // Include CSRF token if available and this is a state-changing request
        const isStateChanging = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);
        
        if (csrfToken && isStateChanging) {
            headers['X-CSRF-Token'] = csrfToken;
        } else if (isStateChanging && !csrfToken) {
            console.warn('[csrfFetch] WARNING: State-changing', method, 'request WITHOUT CSRF token!');
        }

        const fetchOptions: RequestInit = {
            ...options,
            headers: Object.keys(headers).length > 0 ? headers : undefined
        };

        const response = await fetch(url, fetchOptions);

        // Extract token from response for future requests
        extractCSRFToken(response);

        if (!response.ok) {
            console.error('[csrfFetch] Response not OK:', {
                status: response.status,
                statusText: response.statusText,
                url
            });
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return response;
    } catch (error) {
        // Re-throw network and HTTP errors as-is
        throw error;
    }
}

